import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { userApi } from "../accounts/authuser";
import type { PaginatedUsersResponse } from "./follows";

/**
 * `GET /api/admin/users?q=&limit=&offset=` のクエリ。
 *
 * master_admin 専用エンドポイントで、`AuthUser.is_active=True` の全ロール
 * （`master_admin` / `master_client` / `curator` / `admin`）を横断検索する。
 * 既存の `searchUsers` (`/api/users/`) は `role==CREATOR` 限定なので、
 * 残高付与のように受託者でないユーザーも対象に含む UI ではこちらを使う。
 *
 * レスポンス形は `PaginatedUsersResponse`（`searchUsers` と同型）。
 */
export interface SearchAdminUsersParams {
  q?: string;
  limit?: number;
  offset?: number;
}

export const buildSearchAdminUsersQueryString = ({
  q,
  limit,
  offset,
}: SearchAdminUsersParams): string => {
  const params: Record<string, string> = {};
  if (q !== undefined && q !== "") params.q = q;
  if (limit !== undefined) params.limit = String(limit);
  if (offset !== undefined) params.offset = String(offset);
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : "";
};

/**
 * `POST /api/admin/users/<user_id>/balance/credit` のリクエスト。
 *
 * 指定ユーザーの新モデル残高 `balance_free`（税抜・整数円）を ledger 経由で
 * 増額する。実体は `payments.escrow_service.admin_credit` で
 * `PaymentLedger(event=admin_credit, external→free, created_by=操作者)`
 * と `balance_free += amount` を**同一トランザクション**で実行する。
 *
 * `idempotency_key` を指定すると同一キーの再送が安全に無視される（DB ユニーク
 * 制約）。省略時はサーバ側で `admin_credit:<user_id>:<short_uuid>` を生成する。
 */
export interface CreditUserBalanceParams {
  userId: number;
  amount: number;
  note?: string;
  idempotencyKey?: string;
}

export interface CreditUserBalanceResponse {
  status: string;
  data: {
    user: {
      id: number;
      balance_free: string;
    };
    ledger: {
      id: string;
      event_type: string;
      amount: string;
      idempotency_key: string;
      created_by: number;
      note: string | null;
    };
  };
}

export const adminBalanceApi = createApi({
  reducerPath: "adminBalanceApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["AdminUserSearch"],
  endpoints: (builder) => ({
    searchAdminUsers: builder.query<
      PaginatedUsersResponse,
      SearchAdminUsersParams
    >({
      query: (params) =>
        `/api/admin/users${buildSearchAdminUsersQueryString(params)}`,
      providesTags: (_result, _error, { q, limit, offset }) => [
        {
          type: "AdminUserSearch",
          id: `${q ?? ""}:${limit ?? ""}:${offset ?? ""}`,
        },
      ],
    }),

    creditUserBalance: builder.mutation<
      CreditUserBalanceResponse,
      CreditUserBalanceParams
    >({
      query: ({ userId, amount, note, idempotencyKey }) => {
        const body: Record<string, unknown> = { amount };
        if (note !== undefined && note !== "") body.note = note;
        if (idempotencyKey !== undefined && idempotencyKey !== "") {
          body.idempotency_key = idempotencyKey;
        }
        return {
          url: `/api/admin/users/${userId}/balance/credit`,
          method: "POST",
          body,
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // 付与対象が自分自身というケースもあり得るため、念のため
          // /api/me を無効化する（操作者本人の残高表示を最新に保つ）。
          dispatch(userApi.util.invalidateTags(["CurrentUser"]));
        } catch {
          // 失敗時はキャッシュを変更しない。
        }
      },
    }),
  }),
});

export const {
  useSearchAdminUsersQuery,
  useLazySearchAdminUsersQuery,
  useCreditUserBalanceMutation,
} = adminBalanceApi;
